import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 24px 12px 32px;
  }
`;

const Header = styled.div`
  background: white;
  padding: 20px 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;

  h1 {
    margin: 0;
    font-size: 1.4rem;
    color: #333;
    font-weight: 600;
  }
`;

const Filters = styled.div`
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 12px 14px;
  border: 2px solid #eee;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: border-color .2s;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const Select = styled.select`
  min-width: 180px;
  padding: 12px 14px;
  border: 2px solid #eee;
  border-radius: 10px;
  font-size: 0.95rem;
  background: #fff;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const Card = styled(Link)`
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  min-height: 340px;
  text-decoration: none;
  color: inherit;
`;

const Thumb = styled.div`
  position: relative;
  padding-top: 60%;
  background: ${({src}) => `url(${src}) center/cover no-repeat`};
`;

const Status = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 6px 10px;
  border-radius: 14px;
  background: ${({$active}) => $active ? '#e6f4ea' : '#ffebee'};
  color: ${({$active}) => $active ? '#1e7e34' : '#c62828'};
  font-weight: 600;
  font-size: 0.8rem;
`;

const TypeBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 6px 10px;
  border-radius: 14px;
  background: ${({$type}) => {
    if ($type === 'COMPRAR') return '#f0f4ff';
    if ($type === 'ARRENDAR') return '#e6f4ea';
    if ($type === 'FÉRIAS') return '#fff9e6';
    return '#f0f4ff';
  }};
  color: ${({$type}) => {
    if ($type === 'COMPRAR') return '#4a6cf7';
    if ($type === 'ARRENDAR') return '#1e7e34';
    if ($type === 'FÉRIAS') return '#f57c00';
    return '#4a6cf7';
  }};
  font-weight: 600;
  font-size: 0.8rem;
`;

const Body = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: #333;
`;

const Owner = styled.div`
  font-size: 0.88rem;
  color: #555;
`;

const Location = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const Price = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 14px;
`;

const ToggleButton = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? '#ffebe6' : '#e6f4ea')};
  color: ${({ $active }) => ($active ? '#c62828' : '#1e7e34')};
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.12);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  }
`;

const EmptyState = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  text-align: center;
  color: #666;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
`;

const AllListings = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [tipo, setTipo] = useState('');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const doFetch = async (withToken) => {
          const params = new URLSearchParams({
            page: '1',
            pageSize: '50'
          });
          
          // Adiciona parâmetro tipo se o backend suportar
          // if (tipo) {
          //   params.append('tipo', tipo.toLowerCase());
          // }
          
          const resp = await fetch(`${import.meta.env.VITE_URL_API}/imoveis?${params.toString()}`, {
            headers: {
              accept: 'application/json',
              ...(withToken && token ? { Authorization: `Bearer ${token}` } : {})
            }
          });
          return resp.json();
        };

        let data = await doFetch(true);
        // Se vazio e temos token (pode ter filtro de user), tenta sem token (backend retorna todos sem auth)
        const getArray = (d) => {
          if (Array.isArray(d)) return d;
          if (Array.isArray(d?.items)) return d.items;
          if (Array.isArray(d?.Registros)) return d.Registros;
          if (Array.isArray(d?.data)) return d.data;
          return [];
        };

        let arr = getArray(data);
        if ((!arr || arr.length === 0) && token) {
          try {
            data = await doFetch(false);
            arr = getArray(data);
          } catch {
            // Ignora erro ao tentar buscar sem token
          }
        }

        // Filtra itens excluídos (soft delete) antes de mapear
        const filteredArr = (arr || []).filter(imovel => {
          const isExcludedFlag = Number(imovel?.excluidoIMOVEL) === 1 || Number(imovel?.deletadoIMOVEL) === 1;
          const isDeletedAt = !!imovel?.deleted_at || !!imovel?.deletedAt;
          const isInactiveDeleted = Number(imovel?.ativoIMOVEL) === -1;
          // Retorna todos os anúncios não excluídos (ativos e inativos)
          // Valor 0 é Inativo (não excluído), valor -1 é excluído
          return !(isExcludedFlag || isDeletedAt || isInactiveDeleted);
        });

        const mapped = filteredArr.map(imovel => {
          const owner = (() => {
            const candidates = [
              imovel?.anuncianteNome,
              imovel?.cliente?.nomeUSUARIO,
              imovel?.usuario?.nomeUSUARIO,
              imovel?.usuario?.nomeUsuario,
              imovel?.usuario?.nome,
              imovel?.usuario?.name,
              imovel?.usuario?.fullName,
              imovel?.usuario?.username,
              imovel?.proprietario?.nome,
              imovel?.proprietario?.nomeUSUARIO,
              imovel?.proprietario?.name,
              imovel?.proprietario?.fullName,
              imovel?.anunciante?.nome,
              imovel?.anunciante?.nomeUSUARIO,
              imovel?.anunciante?.name,
              imovel?.anunciante?.fullName,
              imovel?.host?.nome,
              imovel?.host?.name,
              imovel?.host?.fullName,
              imovel?.agente?.nome,
            ];

            const name = candidates.find((c) => typeof c === 'string' && c.trim().length > 0);
            if (name) return name;

            const idFallback =
              imovel?.anunciante?.codigo
              || imovel?.anunciante?.id
              || imovel?.codigoUSUARIO
              || imovel?.idUSUARIO
              || imovel?.userId
              || imovel?.idUsuario
              || imovel?.idUSUARIO
              || imovel?.id;

            return idFallback ? `Usuário ${idFallback}` : '';
          })();

          // Mapear o tipo de anúncio
          const getTipoLabel = (imovel) => {
            const tipoId = imovel?.tipoIMOVEL || imovel?.tipoImovel || imovel?.tipo_imovel;
            const tipoSlug = imovel?.tipo?.slugTIPOIMOVEL || imovel?.tipoSlug || '';
            const tipoNome = imovel?.tipo?.nomeTIPOIMOVEL || imovel?.tipoNome || '';
            
            // Se tiver slug, usa para determinar o tipo
            if (tipoSlug) {
              const slugLower = tipoSlug.toLowerCase();
              if (slugLower === 'comprar' || slugLower === 'buy' || slugLower === 'venda' || slugLower === 'sell') {
                return 'COMPRAR';
              }
              if (slugLower === 'arrendar' || slugLower === 'rent' || slugLower === 'aluguel' || slugLower === 'alugar') {
                return 'ARRENDAR';
              }
              if (slugLower === 'ferias' || slugLower === 'vacation' || slugLower === 'férias' || slugLower === 'temporada' || slugLower === 'seasonal' || slugLower === 'holiday') {
                return 'FÉRIAS';
              }
            }
            
            // Se tiver nome, tenta extrair do nome
            if (tipoNome) {
              const nomeLower = tipoNome.toLowerCase();
              if (nomeLower.includes('comprar') || nomeLower.includes('venda') || nomeLower.includes('buy') || nomeLower.includes('sell')) {
                return 'COMPRAR';
              }
              if (nomeLower.includes('arrendar') || nomeLower.includes('aluguel') || nomeLower.includes('rent')) {
                return 'ARRENDAR';
              }
              if (nomeLower.includes('férias') || nomeLower.includes('ferias') || nomeLower.includes('vacation') || nomeLower.includes('temporada') || nomeLower.includes('holiday')) {
                return 'FÉRIAS';
              }
            }
            
            // Se tiver ID numérico, faz mapeamento
            if (tipoId) {
              const idNum = Number(tipoId);
              // Mapeamento comum (pode variar conforme a API)
              if (idNum === 1) return 'COMPRAR';
              if (idNum === 2) return 'ARRENDAR';
              if (idNum === 3) return 'FÉRIAS';
            }
            
            // Fallback: retorna o nome original se existir, ou vazio
            return tipoNome || '';
          };

          return ({
            id: imovel.codigoIMOVEL,
            slug: imovel.slugIMOVEL || imovel.slug || '',
            title: imovel.tituloIMOVEL,
            location: `${imovel.enderecoIMOVEL || ''}${imovel.cityIMOVEL ? ', ' + imovel.cityIMOVEL : ''}${imovel.stateIMOVEL ? ', ' + imovel.stateIMOVEL : ''}`,
            price: imovel.valorIMOVEL ? `€ ${Number(imovel.valorIMOVEL).toLocaleString('pt-PT')}` : '',
            active: Number(imovel.ativoIMOVEL) === 1,
            status: Number(imovel.ativoIMOVEL) === 1 ? 'Ativo' : 'Inativo',
            owner,
            image: imovel.fotos?.[0]?.urlFOTO || imovel.fotos?.[0]?.url || imovel.thumbnail || '',
            type: getTipoLabel(imovel),
          });
        });
        setListings(mapped);
      } catch {
        setListings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    return listings.filter(item => {
      const matchesSearch = search.trim().length === 0 || item.title?.toLowerCase().includes(search.toLowerCase()) || item.location?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !status || item.status.toLowerCase() === status.toLowerCase();
      const matchesTipo = !tipo || item.type === tipo;
      return matchesSearch && matchesStatus && matchesTipo;
    });
  }, [search, status, tipo, listings]);

  const handleToggle = async (id, currentActive) => {
    try {
      setTogglingId(id);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ ativoIMOVEL: currentActive ? 0 : 1 })
      });
      if (!response.ok) throw new Error('Erro ao atualizar status');
      setListings(prev => prev.map(item => item.id === id ? {
        ...item,
        active: !currentActive,
        status: !currentActive ? 'Ativo' : 'Inativo'
      } : item));
    } catch (err) {
      alert(err?.message || 'Não foi possível atualizar o status.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Todos os Anúncios</h1>
      </Header>

      <Filters>
        <SearchInput
          placeholder="Buscar por título ou localização"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos os Status</option>
          <option value="Ativo">Ativo</option>
          <option value="Inativo">Inativo</option>
        </Select>
        <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          <option value="COMPRAR">Comprar</option>
          <option value="ARRENDAR">Arrendar</option>
          <option value="FÉRIAS">Férias</option>
        </Select>
      </Filters>

      {loading ? (
        <EmptyState>Carregando anúncios...</EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>Nenhum anúncio encontrado.</EmptyState>
      ) : (
        <Grid>
          {filtered.map(item => (
            <Card
              key={item.id}
              to={`/property/${item.id}${item.slug ? `/${encodeURIComponent(item.slug)}` : ''}`}
            >
              <Thumb src={item.image}>
                {item.type && <TypeBadge $type={item.type}>{item.type}</TypeBadge>}
                <Status $active={item.active}>{item.status}</Status>
              </Thumb>
              <Body>
                <Title>{item.title}</Title>
                <Owner>{item.owner ? `Anunciante: ${item.owner}` : 'Anunciante: —'}</Owner>
                <Location>{item.location}</Location>
                <Price>{item.price}</Price>
              </Body>
              <Actions>
                <ToggleButton
                  type="button"
                  $active={item.active}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (togglingId && togglingId !== item.id) return;
                    handleToggle(item.id, item.active);
                  }}
                  disabled={togglingId === item.id}
                >
                  {togglingId === item.id ? 'Atualizando...' : item.active ? 'Desativar' : 'Ativar'}
                </ToggleButton>
              </Actions>
            </Card>
          ))}
        </Grid>
      )}
    </PageContainer>
  );
};

export default AllListings;
